"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { toZonedTime, fromZonedTime, getTimezoneOffset } from "date-fns-tz";
import { Loader } from "@googlemaps/js-api-loader";
import AlertDialog from "./AlertDialog";

// Single shared loader instance — prevents duplicate script injection and
// the "Loader must not be called again with different options" error.
const mapsLoader = new Loader({
  apiKey: "AIzaSyBceM5xE-Rvc78bFnTAsOavA9qjjOGegoo",
  version: "weekly",
  libraries: ["geometry", "places", "routes"],
});

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calculatePolylineDistance = (coords) => {
  if (!coords || coords.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineDistance(
      coords[i - 1].lat,
      coords[i - 1].lng,
      coords[i].lat,
      coords[i].lng,
    );
  }
  return total;
};

// Assigns real timestamps to a routed (road-snapped) path by interpolating
// between the segment's departure and arrival times, weighted by distance
// along the geometry. This is what lets the animation clock track the
// worker's ACTUAL GPS timeline instead of a synthetic one.
const interpolateSegmentTimestamps = (path, startTs, endTs) => {
  if (!path || path.length === 0) return [];
  const s = typeof startTs === "number" ? startTs : new Date(startTs).getTime();
  const e = typeof endTs === "number" ? endTs : new Date(endTs).getTime();
  const span = Math.max(e - s, 0);
  if (path.length === 1) return [{ ...path[0], timestamp: s }];

  const cum = [0];
  for (let i = 1; i < path.length; i++) {
    cum.push(
      cum[i - 1] +
        haversineDistance(
          path[i - 1].lat,
          path[i - 1].lng,
          path[i].lat,
          path[i].lng,
        ),
    );
  }
  const total = cum[cum.length - 1];
  return path.map((p, i) => ({
    ...p,
    timestamp: total > 0 ? Math.round(s + (cum[i] / total) * span) : s,
  }));
};

const findDominantCluster = (points) => {
  if (points.length < 3) return null;
  const lats = points.map((p) => p.lat).sort((a, b) => a - b);
  const lngs = points.map((p) => p.lng).sort((a, b) => a - b);
  const medianLat = lats[Math.floor(lats.length / 2)];
  const medianLng = lngs[Math.floor(lngs.length / 2)];
  const dists = points.map((p) =>
    haversineDistance(medianLat, medianLng, p.lat, p.lng),
  );
  const sortedDists = [...dists].sort((a, b) => a - b);
  const p70 = sortedDists[Math.floor(sortedDists.length * 0.7)];
  return { lat: medianLat, lng: medianLng, radius: Math.max(p70, 50) };
};

const dedupByTimestamp = (points) => {
  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (const p of sorted) {
    const last = result[result.length - 1];
    if (!last) {
      result.push(p);
      continue;
    }
    if (
      Math.abs(p.timestamp - last.timestamp) < 100 &&
      haversineDistance(p.lat, p.lng, last.lat, last.lng) < 1
    ) {
      continue;
    }
    result.push(p);
  }
  return result;
};

// Detect whether the locations array has meaningful speed/accuracy
// metadata. Need at least 50% of points carrying the field to trust it.
const hasSpeedAccuracyMetadata = (locations) => {
  if (!locations.length) return false;
  const withMeta = locations.filter(
    (loc) => loc.speed !== undefined || loc.accuracy !== undefined,
  ).length;
  return withMeta / locations.length >= 0.5;
};

// PRE-FILTER
//   WITH metadata: drop points with accuracy > 20m or impossible/sentinel
//     speed values.
//   WITHOUT metadata: geometric spike pre-filter (both edges > 40 m/s AND
//     detour > 200m longer than direct → GPS teleport).
const preFilterByAccuracy = (locations) => {
  const ACCURACY_THRESHOLD = 20;
  const IMPOSSIBLE_SPEED_MPS = 50;
  const GEO_MAX_SPEED_MPS = 40;
  const GEO_MIN_DETOUR_M = 200;

  if (hasSpeedAccuracyMetadata(locations)) {
    const filtered = locations.filter((loc) => {
      if (loc.accuracy !== undefined && loc.accuracy > ACCURACY_THRESHOLD)
        return false;
      if (
        loc.speed !== undefined &&
        (loc.speed < 0 || loc.speed > IMPOSSIBLE_SPEED_MPS)
      )
        return false;
      return true;
    });
    // Only fall back to raw if filtering produced NOTHING at all.
    return filtered.length >= 1 ? filtered : locations;
  }

  const pts = locations.map((loc) => ({
    loc,
    lat: loc.latitude,
    lng: loc.longitude,
    ts: new Date(loc.timestamp).getTime(),
  }));

  const keep = pts.filter((curr, i) => {
    if (i === 0 || i === pts.length - 1) return true;
    const prev = pts[i - 1];
    const next = pts[i + 1];
    const dIn = haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng);
    const dOut = haversineDistance(curr.lat, curr.lng, next.lat, next.lng);
    const dtIn = Math.max((curr.ts - prev.ts) / 1000, 1);
    const dtOut = Math.max((next.ts - curr.ts) / 1000, 1);
    const speedIn = dIn / dtIn;
    const speedOut = dOut / dtOut;
    const dDirect = haversineDistance(prev.lat, prev.lng, next.lat, next.lng);
    const detour = dIn + dOut;
    const isSpike =
      speedIn > GEO_MAX_SPEED_MPS &&
      speedOut > GEO_MAX_SPEED_MPS &&
      detour > dDirect * 1.5 &&
      detour - dDirect > GEO_MIN_DETOUR_M;
    return !isSpike;
  });

  const result = keep.map((p) => p.loc);
  return result.length >= 1 ? result : locations;
};

const rejectSpikeOutliers = (points) => {
  if (points.length < 3) return points;
  let current = [...points];

  // PASS 0: physically impossible edges (>150km/h)
  const IMPOSSIBLE_SPEED_MPS = 42;
  for (let pass = 0; pass < 30; pass++) {
    const impossibleEdges = [];
    for (let i = 1; i < current.length; i++) {
      const d = haversineDistance(
        current[i - 1].lat,
        current[i - 1].lng,
        current[i].lat,
        current[i].lng,
      );
      const dt = (current[i].timestamp - current[i - 1].timestamp) / 1000;
      if (dt > 0 && d / dt > IMPOSSIBLE_SPEED_MPS) impossibleEdges.push(i);
    }
    if (impossibleEdges.length === 0) break;

    const removalCandidates = new Set();
    const MIN_TELEPORT_DIST_M = 300;
    for (const i of impossibleEdges) {
      const prev = current[i - 1];
      const curr = current[i];
      const edgeDist = haversineDistance(
        prev.lat,
        prev.lng,
        curr.lat,
        curr.lng,
      );

      if (edgeDist >= MIN_TELEPORT_DIST_M) {
        removalCandidates.add(i);
      } else {
        const score = (idx) => {
          let mLat = 0,
            mLng = 0,
            count = 0;
          for (
            let k = Math.max(0, idx - 4);
            k <= Math.min(current.length - 1, idx + 4);
            k++
          ) {
            if (k === idx) continue;
            mLat += current[k].lat;
            mLng += current[k].lng;
            count++;
          }
          if (count === 0) return 0;
          mLat /= count;
          mLng /= count;
          return haversineDistance(
            mLat,
            mLng,
            current[idx].lat,
            current[idx].lng,
          );
        };
        const scorePrev = score(i - 1);
        const scoreCurr = score(i);
        removalCandidates.add(scorePrev > scoreCurr ? i - 1 : i);
      }
    }

    if (removalCandidates.size === 0) break;
    current = current.filter((_, idx) => !removalCandidates.has(idx));
  }

  // PASS A: cluster-aware rejection (catches on-site jitter excursions)
  const cluster = findDominantCluster(current);
  if (cluster) {
    const distsFromCluster = current.map((p) =>
      haversineDistance(cluster.lat, cluster.lng, p.lat, p.lng),
    );
    const inCluster = distsFromCluster.filter((d) => d < cluster.radius).length;
    const concentration = inCluster / current.length;

    if (concentration >= 0.6) {
      const OUTLIER_THRESHOLD = Math.max(cluster.radius * 4, 200);
      const filtered = [];
      for (let i = 0; i < current.length; i++) {
        const distFromCluster = distsFromCluster[i];
        if (distFromCluster <= OUTLIER_THRESHOLD) {
          filtered.push(current[i]);
          continue;
        }
        let consecutiveFarBefore = 0;
        let consecutiveFarAfter = 0;
        for (let j = i - 1; j >= 0 && j >= i - 10; j--) {
          if (distsFromCluster[j] > OUTLIER_THRESHOLD) consecutiveFarBefore++;
          else break;
        }
        for (let j = i + 1; j < current.length && j <= i + 10; j++) {
          if (distsFromCluster[j] > OUTLIER_THRESHOLD) consecutiveFarAfter++;
          else break;
        }
        const excursionStartIdx = i - consecutiveFarBefore;
        const excursionEndIdx = i + consecutiveFarAfter;
        const excursionDuration =
          current[excursionEndIdx].timestamp -
          current[excursionStartIdx].timestamp;
        const consecutiveFar = consecutiveFarBefore + consecutiveFarAfter + 1;
        const isRealExcursion =
          consecutiveFar >= 4 && excursionDuration >= 60_000;
        if (isRealExcursion) filtered.push(current[i]);
      }
      current = filtered;
    }
  }

  // PASS B: classic spike detection (out-and-back), multi-iteration
  for (let pass = 0; pass < 5; pass++) {
    if (current.length < 3) break;
    const filtered = [current[0]];
    let removed = 0;
    for (let i = 1; i < current.length - 1; i++) {
      const prev = filtered[filtered.length - 1];
      const candidate = current[i];
      const next = current[i + 1];
      const dPrevToCand = haversineDistance(
        prev.lat,
        prev.lng,
        candidate.lat,
        candidate.lng,
      );
      const dCandToNext = haversineDistance(
        candidate.lat,
        candidate.lng,
        next.lat,
        next.lng,
      );
      const dPrevToNext = haversineDistance(
        prev.lat,
        prev.lng,
        next.lat,
        next.lng,
      );
      const dtPrevToCand = (candidate.timestamp - prev.timestamp) / 1000;
      const dtCandToNext = (next.timestamp - candidate.timestamp) / 1000;
      const speedIn = dtPrevToCand > 0 ? dPrevToCand / dtPrevToCand : 0;
      const speedOut = dtCandToNext > 0 ? dCandToNext / dtCandToNext : 0;
      const detour = dPrevToCand + dCandToNext;
      const detourRatio = dPrevToNext > 1 ? detour / dPrevToNext : Infinity;

      const isSpike =
        (detourRatio > 2.5 && detour - dPrevToNext > 80) ||
        Math.max(speedIn, speedOut) > 35 ||
        (dPrevToCand > 150 && dCandToNext > 150 && dPrevToNext < 100);

      if (isSpike) removed++;
      else filtered.push(candidate);
    }
    filtered.push(current[current.length - 1]);
    if (removed === 0) break;
    current = filtered;
  }

  return current;
};

const checkGeometryStationary = (points) => {
  const TIGHT_RADIUS_M = 40;
  const TIGHT_CONCENTRATION = 0.9;
  const LOOSE_RADIUS_M = 80;
  const LOOSE_CONCENTRATION = 0.92;
  const LOOSE_MAX_BBOX_M = 150;
  const MIN_POINTS = 6;

  if (points.length < MIN_POINTS) return false;

  const cluster = findDominantCluster(points);
  if (!cluster) return false;

  const inTight = points.filter(
    (p) =>
      haversineDistance(cluster.lat, cluster.lng, p.lat, p.lng) <=
      TIGHT_RADIUS_M,
  ).length;
  const inLoose = points.filter(
    (p) =>
      haversineDistance(cluster.lat, cluster.lng, p.lat, p.lng) <=
      LOOSE_RADIUS_M,
  ).length;

  const concTight = inTight / points.length;
  const concLoose = inLoose / points.length;

  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const bbox = haversineDistance(
    Math.min(...lats),
    Math.min(...lngs),
    Math.max(...lats),
    Math.max(...lngs),
  );

  return (
    concTight >= TIGHT_CONCENTRATION ||
    (concLoose >= LOOSE_CONCENTRATION && bbox <= LOOSE_MAX_BBOX_M)
  );
};

const trimIdleEdges = (points, radiusM = 40) => {
  if (points.length < 3) return { trimmed: points, leadTrim: 0, tailTrim: 0 };

  const first = points[0];
  let leadEnd = 0;
  for (let i = 0; i < points.length; i++) {
    const d = haversineDistance(
      first.lat,
      first.lng,
      points[i].lat,
      points[i].lng,
    );
    if (d > radiusM) {
      leadEnd = Math.max(0, i - 1);
      break;
    }
    leadEnd = i;
  }

  const last = points[points.length - 1];
  let tailStart = points.length - 1;
  for (let i = points.length - 1; i >= 0; i--) {
    const d = haversineDistance(
      last.lat,
      last.lng,
      points[i].lat,
      points[i].lng,
    );
    if (d > radiusM) {
      tailStart = Math.min(points.length - 1, i + 1);
      break;
    }
    tailStart = i;
  }

  if (leadEnd >= tailStart) {
    return { trimmed: points, leadTrim: 0, tailTrim: 0 };
  }

  const trimmed = points.slice(leadEnd, tailStart + 1);
  return {
    trimmed,
    leadTrim: leadEnd,
    tailTrim: points.length - 1 - tailStart,
  };
};

// Cross-checked "is this point credibly slow/stationary" — looks at actual
// position deltas to neighbors, not just the reported speed field, so a
// single stale speed:0 reading mid-highway can't fake a stop.
const isCrediblyLowSpeed = (points, idx) => {
  const IMPLIED_SPEED_TRUST_MPS = 3;
  const p = points[idx];
  const reportedLow = p.speed === undefined || p.speed < 1.5;
  if (!reportedLow) return false;

  let impliedFast = false;
  if (idx > 0) {
    const prev = points[idx - 1];
    const d = haversineDistance(prev.lat, prev.lng, p.lat, p.lng);
    const dt = (p.timestamp - prev.timestamp) / 1000;
    if (dt > 0 && d / dt > IMPLIED_SPEED_TRUST_MPS) impliedFast = true;
  }
  if (!impliedFast && idx < points.length - 1) {
    const next = points[idx + 1];
    const d = haversineDistance(p.lat, p.lng, next.lat, next.lng);
    const dt = (next.timestamp - p.timestamp) / 1000;
    if (dt > 0 && d / dt > IMPLIED_SPEED_TRUST_MPS) impliedFast = true;
  }
  return !impliedFast;
};

const identifyStops = (points) => {
  const STOP_RADIUS_M = 60;
  const STOP_MIN_DURATION_MS = 90_000;
  const SAME_PLACE_RADIUS_M = 80;
  const REASONABLE_GAP_RADIUS_M = 120;
  const REASONABLE_GAP_MS = 30 * 60_000;
  const TIME_CONTAINMENT_ABSORB_MAX_M = 120;
  const SWEEP_RADIUS_M = 100;
  const MIN_STOP_MEMBERS_FOR_TRUST = 3;

  const n = points.length;
  const assigned = new Array(n).fill(false);

  const recomputeCentroid = (stop) => {
    let cLat = 0,
      cLng = 0;
    for (const m of stop.members) {
      cLat += points[m].lat;
      cLng += points[m].lng;
    }
    cLat /= stop.members.length;
    cLng /= stop.members.length;
    stop.centerLat = cLat;
    stop.centerLng = cLng;
  };

  // PHASE 1: form raw stops from dwell time + tight radius, cross-checked
  // against credible speed evidence when available.
  const rawStops = [];
  let i = 0;
  while (i < n) {
    if (assigned[i]) {
      i++;
      continue;
    }

    let cLat = points[i].lat;
    let cLng = points[i].lng;
    let members = [i];

    for (let j = i + 1; j < n; j++) {
      if (assigned[j]) continue;
      const d = haversineDistance(cLat, cLng, points[j].lat, points[j].lng);

      if (d <= STOP_RADIUS_M) {
        members.push(j);
        cLat = 0;
        cLng = 0;
        for (const m of members) {
          cLat += points[m].lat;
          cLng += points[m].lng;
        }
        cLat /= members.length;
        cLng /= members.length;
      } else {
        let consecutiveFar = 1;
        let kEnd = j;
        for (let k = j + 1; k < Math.min(n, j + 6); k++) {
          const dk = haversineDistance(
            cLat,
            cLng,
            points[k].lat,
            points[k].lng,
          );
          if (dk > STOP_RADIUS_M) {
            consecutiveFar++;
            kEnd = k;
          } else break;
        }
        const farDuration = points[kEnd].timestamp - points[j].timestamp;
        if (consecutiveFar >= 3 || farDuration >= 60_000) break;
      }
    }

    const duration =
      points[members[members.length - 1]].timestamp -
      points[members[0]].timestamp;

    const lowSpeedMembers = members.filter((m) =>
      isCrediblyLowSpeed(points, m),
    ).length;
    const isLikelyStationary =
      members.length >= 5 || lowSpeedMembers >= members.length * 0.8;

    const smallStopNeedsFullConfidence =
      members.length < MIN_STOP_MEMBERS_FOR_TRUST &&
      lowSpeedMembers < members.length;

    if (
      duration >= STOP_MIN_DURATION_MS &&
      members.length >= 3 &&
      isLikelyStationary &&
      !smallStopNeedsFullConfidence
    ) {
      for (const m of members) assigned[m] = true;
      rawStops.push({
        centerLat: cLat,
        centerLng: cLng,
        members,
        startTs: points[members[0]].timestamp,
        endTs: points[members[members.length - 1]].timestamp,
      });
    }
    i++;
  }

  // PHASE 2: merge same-location stops
  let merged = true;
  while (merged) {
    merged = false;
    for (let a = 0; a < rawStops.length; a++) {
      for (let b = a + 1; b < rawStops.length; b++) {
        const d = haversineDistance(
          rawStops[a].centerLat,
          rawStops[a].centerLng,
          rawStops[b].centerLat,
          rawStops[b].centerLng,
        );
        const gap = Math.abs(rawStops[b].startTs - rawStops[a].endTs);
        if (
          d <= SAME_PLACE_RADIUS_M ||
          (d <= REASONABLE_GAP_RADIUS_M && gap <= REASONABLE_GAP_MS)
        ) {
          rawStops[a].members = [
            ...rawStops[a].members,
            ...rawStops[b].members,
          ].sort((x, y) => x - y);
          rawStops[a].startTs = Math.min(
            rawStops[a].startTs,
            rawStops[b].startTs,
          );
          rawStops[a].endTs = Math.max(rawStops[a].endTs, rawStops[b].endTs);
          recomputeCentroid(rawStops[a]);
          rawStops.splice(b, 1);
          merged = true;
          break;
        }
      }
      if (merged) break;
    }
  }

  // PHASE 3: absorb time-contained stops (tight radius only)
  let absorbed = true;
  while (absorbed) {
    absorbed = false;
    for (let a = 0; a < rawStops.length; a++) {
      for (let b = 0; b < rawStops.length; b++) {
        if (a === b) continue;
        const A = rawStops[a],
          B = rawStops[b];
        const bInsideA = B.startTs >= A.startTs && B.endTs <= A.endTs;
        if (!bInsideA) continue;
        const d = haversineDistance(
          A.centerLat,
          A.centerLng,
          B.centerLat,
          B.centerLng,
        );
        if (d > TIME_CONTAINMENT_ABSORB_MAX_M) continue;
        A.members = [...A.members, ...B.members].sort((x, y) => x - y);
        recomputeCentroid(A);
        rawStops.splice(b, 1);
        absorbed = true;
        break;
      }
      if (absorbed) break;
    }
  }

  // PHASE 4: sweep unassigned points near each stop
  for (const stop of rawStops) {
    for (let p = 0; p < n; p++) {
      if (assigned[p]) continue;
      if (points[p].timestamp < stop.startTs - 30_000) continue;
      if (points[p].timestamp > stop.endTs + 30_000) continue;
      const d = haversineDistance(
        stop.centerLat,
        stop.centerLng,
        points[p].lat,
        points[p].lng,
      );
      if (d <= SWEEP_RADIUS_M) {
        stop.members.push(p);
        assigned[p] = true;
      }
    }
    stop.members.sort((x, y) => x - y);
    stop.startTs = Math.min(...stop.members.map((m) => points[m].timestamp));
    stop.endTs = Math.max(...stop.members.map((m) => points[m].timestamp));
    recomputeCentroid(stop);
  }

  rawStops.sort((a, b) => a.startTs - b.startTs);
  return { stops: rawStops };
};

const routeBetweenAnchors = async (from, to) => {
  try {
    const directionsService = new window.google.maps.DirectionsService();
    const result = await new Promise((resolve) => {
      directionsService.route(
        {
          origin: new window.google.maps.LatLng(from.lat, from.lng),
          destination: new window.google.maps.LatLng(to.lat, to.lng),
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (res, status) => {
          if (status !== "OK") {
            console.warn(`routeBetweenAnchors: Directions failed (${status})`, {
              from,
              to,
            });
          }
          resolve(status === "OK" ? res : null);
        },
      );
    });
    if (!result) return [];
    const path = [];
    result.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        step.path.forEach((point) => {
          path.push({ lat: point.lat(), lng: point.lng() });
        });
      });
    });
    // Interpolate real timestamps across the routed geometry:
    // departure time of the "from" anchor → arrival time of the "to" anchor.
    const departTs = from.departTs ?? from.timestamp;
    const arriveTs = to.arriveTs ?? to.timestamp;
    return interpolateSegmentTimestamps(path, departTs, arriveTs);
  } catch (err) {
    console.error("routeBetweenAnchors threw:", err);
    return [];
  }
};

// Distance+time-aware downsampling: collapses redundant points BEFORE the
// expensive spike-rejection/stop-detection passes run. Keeps every point
// that represents genuine movement or a genuine time gap; drops the rest.
const downsampleForProcessing = (locations, maxPoints = 1500) => {
  if (locations.length <= maxPoints) return locations;

  const MIN_DIST_M = 8; // collapse points closer than this...
  const MIN_TIME_MS = 5_000; // ...unless enough time has passed anyway

  const kept = [locations[0]];
  for (let i = 1; i < locations.length - 1; i++) {
    const prev = kept[kept.length - 1];
    const curr = locations[i];
    const prevLat = prev.latitude,
      prevLng = prev.longitude;
    const currLat = curr.latitude,
      currLng = curr.longitude;
    if (
      prevLat == null ||
      prevLng == null ||
      currLat == null ||
      currLng == null
    ) {
      continue;
    }

    const d = haversineDistance(prevLat, prevLng, currLat, currLng);
    const prevTs = new Date(prev.timestamp?.$date || prev.timestamp).getTime();
    const currTs = new Date(curr.timestamp?.$date || curr.timestamp).getTime();
    const dt = currTs - prevTs;

    if (d >= MIN_DIST_M || dt >= MIN_TIME_MS) {
      kept.push(curr);
    }
  }
  kept.push(locations[locations.length - 1]);

  // Still too many — fall back to uniform stride sampling.
  if (kept.length > maxPoints) {
    const stride = kept.length / maxPoints;
    const strided = [];
    for (let i = 0; i < maxPoints; i++) {
      strided.push(kept[Math.min(kept.length - 1, Math.round(i * stride))]);
    }
    if (strided[strided.length - 1] !== kept[kept.length - 1]) {
      strided.push(kept[kept.length - 1]);
    }
    return strided;
  }

  return kept;
};

// Main orchestrator: raw {latitude, longitude, timestamp, speed?, accuracy?}
// locations → drawable path. Returns [] when the worker was stationary
// (caller shows a single pin).
const createTimeOrderedRoute = async (rawLocations) => {
  const locations = downsampleForProcessing(rawLocations, 1500);
  const withMeta = hasSpeedAccuracyMetadata(locations);

  // 0. Pre-filter by device accuracy/speed (or geometry when no metadata)
  const accuracyFiltered = preFilterByAccuracy(locations);

  // 1. Canonical points — sanitise negative speed sentinels
  const raw = accuracyFiltered
    .map((loc, idx) => ({
      lat: loc.latitude,
      lng: loc.longitude,
      timestamp: loc.timestamp
        ? new Date(loc.timestamp?.$date || loc.timestamp).getTime()
        : idx * 15_000,
      speed: loc.speed !== undefined && loc.speed >= 0 ? loc.speed : undefined,
      accuracy: loc.accuracy,
    }))
    .filter((p) => p.lat && p.lng && !isNaN(p.timestamp));

  if (raw.length < 2) return [];

  const deduped = dedupByTimestamp(raw);
  const spikesRemoved = rejectSpikeOutliers(deduped);

  // Noise filter — with metadata, also keep points where device reports motion
  let noiseFiltered;
  if (withMeta) {
    noiseFiltered = [spikesRemoved[0]];
    for (let i = 1; i < spikesRemoved.length; i++) {
      const last = noiseFiltered[noiseFiltered.length - 1];
      const curr = spikesRemoved[i];
      const d = haversineDistance(last.lat, last.lng, curr.lat, curr.lng);
      const dt = curr.timestamp - last.timestamp;
      if (d > 2 || dt > 10_000) {
        noiseFiltered.push(curr);
        continue;
      }
      if (curr.speed !== undefined && curr.speed >= 0.5) {
        noiseFiltered.push(curr);
      }
    }
  } else {
    noiseFiltered = [spikesRemoved[0]];
    for (let i = 1; i < spikesRemoved.length; i++) {
      const last = noiseFiltered[noiseFiltered.length - 1];
      const curr = spikesRemoved[i];
      const d = haversineDistance(last.lat, last.lng, curr.lat, curr.lng);
      const dt = curr.timestamp - last.timestamp;
      if (d > 2 || dt > 10_000) noiseFiltered.push(curr);
    }
  }

  if (noiseFiltered.length < 2) return [];

  // ─── STATIONARY CHECK ───
  if (withMeta) {
    const speedPoints = noiseFiltered.filter((p) => p.speed !== undefined);

    if (speedPoints.length >= Math.floor(noiseFiltered.length * 0.5)) {
      const speeds = speedPoints.map((p) => p.speed).sort((a, b) => a - b);
      const medianSpeed = speeds[Math.floor(speeds.length / 2)];
      const maxSpeed = speeds[speeds.length - 1];

      if (medianSpeed < 1.0 && maxSpeed < 4.0) {
        const COLD_START_MIN_DIST_M = 200;
        const firstPtRaw = noiseFiltered[0];
        const secondPt = noiseFiltered[1];

        const clusterForOrigin = findDominantCluster(noiseFiltered.slice(1));
        const firstHasNoMeta =
          firstPtRaw.speed === undefined && firstPtRaw.accuracy === undefined;
        const firstDistFromCluster = clusterForOrigin
          ? haversineDistance(
              firstPtRaw.lat,
              firstPtRaw.lng,
              clusterForOrigin.lat,
              clusterForOrigin.lng,
            )
          : 0;
        const secondDistFromCluster =
          clusterForOrigin && secondPt
            ? haversineDistance(
                secondPt.lat,
                secondPt.lng,
                clusterForOrigin.lat,
                clusterForOrigin.lng,
              )
            : 0;

        const isLegitimateOrigin =
          firstHasNoMeta &&
          firstDistFromCluster > COLD_START_MIN_DIST_M &&
          secondDistFromCluster < COLD_START_MIN_DIST_M;

        if (isLegitimateOrigin && clusterForOrigin) {
          const syntheticDest = {
            lat: clusterForOrigin.lat,
            lng: clusterForOrigin.lng,
            timestamp: secondPt.timestamp,
          };
          const routed = await routeBetweenAnchors(firstPtRaw, syntheticDest);
          return routed.length > 0 ? routed : [firstPtRaw, syntheticDest];
        }

        return [];
      }
    } else if (checkGeometryStationary(noiseFiltered)) {
      return [];
    }
  } else {
    if (checkGeometryStationary(noiseFiltered)) return [];
  }

  const firstPt = noiseFiltered[0];
  const lastPt = noiseFiltered[noiseFiltered.length - 1];

  let maxExcursion = 0;
  for (const p of noiseFiltered) {
    const d = haversineDistance(firstPt.lat, firstPt.lng, p.lat, p.lng);
    if (d > maxExcursion) maxExcursion = d;
  }
  const allLats = noiseFiltered.map((p) => p.lat);
  const allLngs = noiseFiltered.map((p) => p.lng);
  const bboxDiag = haversineDistance(
    Math.min(...allLats),
    Math.min(...allLngs),
    Math.max(...allLats),
    Math.max(...allLngs),
  );

  let isLocalLoop;
  if (withMeta) {
    const speedPts = noiseFiltered.filter((p) => p.speed !== undefined);
    const maxSpeed =
      speedPts.length > 0 ? Math.max(...speedPts.map((p) => p.speed)) : 0;
    const isVehicle = maxSpeed > 8;
    const localThreshold = isVehicle ? 2000 : 500;
    isLocalLoop =
      maxExcursion < localThreshold * 0.8 && bboxDiag < localThreshold;
  } else {
    isLocalLoop = maxExcursion < 800 && bboxDiag < 1000;
  }

  if (isLocalLoop) {
    const { stops: probeStops } = identifyStops(noiseFiltered);
    const DISTINCT_STOP_MIN_DIST_M = 150;
    const hasDistinctAwayStop = probeStops.some(
      (s) =>
        haversineDistance(firstPt.lat, firstPt.lng, s.centerLat, s.centerLng) >
        DISTINCT_STOP_MIN_DIST_M,
    );
    if (hasDistinctAwayStop) isLocalLoop = false;
  }

  if (isLocalLoop) {
    const { trimmed: routablePoints } = trimIdleEdges(noiseFiltered, 40);
    const workingPoints =
      routablePoints.length >= 2 ? routablePoints : noiseFiltered;
    const rawGpsPathLength = calculatePolylineDistance(workingPoints);

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const MAX_WAYPOINTS = 23;

      const bearing = (p1, p2) => {
        const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
        const lat1 = (p1.lat * Math.PI) / 180;
        const lat2 = (p2.lat * Math.PI) / 180;
        const y = Math.sin(dLng) * Math.cos(lat2);
        const x =
          Math.cos(lat1) * Math.sin(lat2) -
          Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
        return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
      };
      const headingDiff = (a, b) => {
        const diff = Math.abs(a - b) % 360;
        return diff > 180 ? 360 - diff : diff;
      };

      const GAP_MS = 3 * 60_000;
      const HEADING_CHANGE_DEG = 25;
      const MIN_DIST_M = 30;

      const candidateIndices = new Set([0, workingPoints.length - 1]);
      for (let i = 1; i < workingPoints.length - 1; i++) {
        const prev = workingPoints[i - 1];
        const curr = workingPoints[i];
        const next = workingPoints[i + 1];

        const dtBefore = curr.timestamp - prev.timestamp;
        const dtAfter = next.timestamp - curr.timestamp;
        if (dtBefore > GAP_MS || dtAfter > GAP_MS) {
          candidateIndices.add(Math.max(0, i - 1));
          candidateIndices.add(i);
          candidateIndices.add(Math.min(workingPoints.length - 1, i + 1));
          continue;
        }

        if (withMeta && curr.speed !== undefined) {
          const prevSpeed = workingPoints[i - 1].speed;
          const nextSpeed = workingPoints[i + 1].speed;
          if (
            prevSpeed !== undefined &&
            nextSpeed !== undefined &&
            Math.abs(curr.speed - prevSpeed) > 3
          ) {
            candidateIndices.add(i);
            continue;
          }
        }

        const distToPrev = haversineDistance(
          prev.lat,
          prev.lng,
          curr.lat,
          curr.lng,
        );
        const distToNext = haversineDistance(
          curr.lat,
          curr.lng,
          next.lat,
          next.lng,
        );
        if (distToPrev < MIN_DIST_M || distToNext < MIN_DIST_M) continue;

        const h1 = bearing(prev, curr);
        const h2 = bearing(curr, next);
        if (headingDiff(h1, h2) > HEADING_CHANGE_DEG) candidateIndices.add(i);
      }

      let selectedIndices = [...candidateIndices].sort((a, b) => a - b);

      const MAX_WAYPOINT_FRACTION = 0.5;
      const maxAllowedWaypoints = Math.max(
        2,
        Math.floor(workingPoints.length * MAX_WAYPOINT_FRACTION),
      );

      const capSelection = (indices, capCount) => {
        if (indices.length <= capCount + 2) return indices;
        const gapBoundaries = new Set([0, workingPoints.length - 1]);
        for (let i = 1; i < workingPoints.length; i++) {
          const dt =
            workingPoints[i].timestamp - workingPoints[i - 1].timestamp;
          if (dt > GAP_MS) {
            gapBoundaries.add(i - 1);
            gapBoundaries.add(i);
          }
        }
        const mustKeep = indices.filter((idx) => gapBoundaries.has(idx));
        const optional = indices.filter((idx) => !gapBoundaries.has(idx));
        const remainingSlots = Math.max(0, capCount + 2 - mustKeep.length);
        const step = optional.length / Math.max(remainingSlots, 1);
        const sampled = Array.from(
          { length: remainingSlots },
          (_, k) =>
            optional[Math.min(Math.round(k * step), optional.length - 1)],
        );
        return [...new Set([...mustKeep, ...sampled])].sort((a, b) => a - b);
      };

      selectedIndices = capSelection(selectedIndices, maxAllowedWaypoints);
      selectedIndices = capSelection(selectedIndices, MAX_WAYPOINTS);

      const selectedPoints = selectedIndices.map((i) => workingPoints[i]);
      const origin = new window.google.maps.LatLng(
        selectedPoints[0].lat,
        selectedPoints[0].lng,
      );
      const destination = new window.google.maps.LatLng(
        selectedPoints[selectedPoints.length - 1].lat,
        selectedPoints[selectedPoints.length - 1].lng,
      );
      const waypoints = selectedPoints.slice(1, -1).map((p) => ({
        location: new window.google.maps.LatLng(p.lat, p.lng),
        stopover: false,
      }));

      const result = await new Promise((resolve) => {
        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
          },
          (res, status) => resolve(status === "OK" ? res : null),
        );
      });

      if (result) {
        const rawPath = [];
        result.routes[0].legs.forEach((leg) => {
          leg.steps.forEach((step) => {
            step.path.forEach((point) => {
              rawPath.push({ lat: point.lat(), lng: point.lng() });
            });
          });
        });
        // Real timeline: first selected GPS point → last selected GPS point
        const path = interpolateSegmentTimestamps(
          rawPath,
          selectedPoints[0].timestamp,
          selectedPoints[selectedPoints.length - 1].timestamp,
        );

        const routedPathLength = calculatePolylineDistance(path);
        const MAX_ROUTED_TO_RAW_RATIO = 4;
        const routedLengthIsSane =
          rawGpsPathLength < 30 ||
          routedPathLength <= rawGpsPathLength * MAX_ROUTED_TO_RAW_RATIO;

        if (routedLengthIsSane && path.length > 0) return path;

        const fallbackResult = await new Promise((resolve) => {
          directionsService.route(
            {
              origin,
              destination,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (res, status) => resolve(status === "OK" ? res : null),
          );
        });
        if (fallbackResult) {
          const rawFallback = [];
          fallbackResult.routes[0].legs.forEach((leg) => {
            leg.steps.forEach((step) => {
              step.path.forEach((point) => {
                rawFallback.push({ lat: point.lat(), lng: point.lng() });
              });
            });
          });
          const fallbackPath = interpolateSegmentTimestamps(
            rawFallback,
            selectedPoints[0].timestamp,
            selectedPoints[selectedPoints.length - 1].timestamp,
          );
          const fallbackLength = calculatePolylineDistance(fallbackPath);
          if (
            fallbackPath.length > 0 &&
            (rawGpsPathLength < 30 ||
              fallbackLength <= rawGpsPathLength * MAX_ROUTED_TO_RAW_RATIO)
          ) {
            return fallbackPath;
          }
        }
        return workingPoints;
      }
    } catch (err) {
      console.error("Local loop routing threw:", err);
    }
    return workingPoints;
  }

  // Longer trips — anchor-based routing via identified stops.
  // Each anchor carries arriveTs/departTs so segments interpolate real time
  // and stop dwell is preserved in the timeline.
  const { stops } = identifyStops(noiseFiltered);

  if (stops.length === 0) {
    const routed = await routeBetweenAnchors(firstPt, lastPt);
    return routed.length > 0 ? routed : noiseFiltered;
  }

  const anchors = [];
  const distStartToFirstStop = haversineDistance(
    firstPt.lat,
    firstPt.lng,
    stops[0].centerLat,
    stops[0].centerLng,
  );
  if (distStartToFirstStop > 100) {
    anchors.push({
      lat: firstPt.lat,
      lng: firstPt.lng,
      arriveTs: firstPt.timestamp,
      departTs: firstPt.timestamp,
    });
  }
  for (const stop of stops) {
    anchors.push({
      lat: stop.centerLat,
      lng: stop.centerLng,
      arriveTs: stop.startTs,
      departTs: stop.endTs,
    });
  }
  const lastStop = stops[stops.length - 1];
  const distEndToLastStop = haversineDistance(
    lastPt.lat,
    lastPt.lng,
    lastStop.centerLat,
    lastStop.centerLng,
  );
  if (distEndToLastStop > 100) {
    anchors.push({
      lat: lastPt.lat,
      lng: lastPt.lng,
      arriveTs: lastPt.timestamp,
      departTs: lastPt.timestamp,
    });
  }

  // Route every leg with capped concurrency. One retry per leg on failure;
  // straight-line fallback (with a logged warning) only if both attempts fail.
  const routeLeg = async (from, to, index) => {
    const directDist = haversineDistance(from.lat, from.lng, to.lat, to.lng);

    if (directDist < 50) {
      return {
        index,
        segment: [{ lat: to.lat, lng: to.lng, timestamp: to.arriveTs }],
        failed: false,
      };
    }

    let routedSegment = await routeBetweenAnchors(from, to);
    if (routedSegment.length === 0) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      routedSegment = await routeBetweenAnchors(from, to);
    }

    if (routedSegment.length > 0) {
      return { index, segment: routedSegment.slice(1), failed: false };
    }

    console.warn(
      `Leg ${index} fell back to a straight line (${(directDist / 1000).toFixed(1)}km) — Directions API failed twice`,
      { from, to },
    );
    return {
      index,
      segment: [{ lat: to.lat, lng: to.lng, timestamp: to.arriveTs }],
      failed: true,
      distanceM: directDist,
    };
  };

  const CONCURRENCY = 5;
  const legResults = new Array(anchors.length - 1);
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(CONCURRENCY, Math.max(anchors.length - 1, 1)) },
    async () => {
      while (cursor < anchors.length - 1) {
        const i = cursor++;
        legResults[i] = await routeLeg(anchors[i], anchors[i + 1], i);
      }
    },
  );
  await Promise.all(workers);

  const finalPath = [];
  const failedLegs = [];
  if (anchors.length >= 2) {
    finalPath.push({
      lat: anchors[0].lat,
      lng: anchors[0].lng,
      timestamp: anchors[0].departTs,
    });
  }
  for (const result of legResults) {
    if (!result) continue;
    finalPath.push(...result.segment);
    if (result.failed) {
      failedLegs.push({
        index: result.index,
        distanceM: result.distanceM,
      });
    }
  }

  if (failedLegs.length > 0) {
    finalPath.__failedLegs = failedLegs;
  }

  return finalPath;
};

// ─────────────────────────────────────────────────────────────────────────
// Shared dark-theme tokens + tiny presentational components
// ─────────────────────────────────────────────────────────────────────────
const INPUT_BASE =
  "w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 text-sm [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-orange-400/60 focus:border-orange-400 hover:border-gray-600 transition disabled:opacity-60 disabled:cursor-not-allowed";
const LABEL_BASE =
  "block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5";
const SECTION_CARD =
  "bg-gray-800/70 rounded-xl border border-gray-700/60 shadow-md";
const SECTION_TITLE = "text-sm font-semibold text-gray-100 tracking-tight";

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Spinner = ({ className = "h-5 w-5" }) => (
  <svg
    className={`animate-spin text-orange-400 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

const StatTile = ({ label, value, unit, accent }) => (
  <div className="bg-gray-900 border border-gray-700/70 rounded-lg px-4 py-2 text-center min-w-[84px]">
    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
      {label}
    </p>
    <p
      className={`text-lg font-bold leading-tight tabular-nums ${
        accent ? "text-orange-400" : "text-gray-100"
      }`}
    >
      {value}
      {unit && (
        <span className="text-xs font-medium text-gray-400 ml-0.5">{unit}</span>
      )}
    </p>
  </div>
);

const CardHeader = ({ title, right }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <span className="w-1 h-4 rounded-full bg-orange-400" />
      <h2 className={SECTION_TITLE}>{title}</h2>
    </div>
    {right}
  </div>
);

const DARK_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#1a1d27" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1d27" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#2d3142" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#6b7280" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#0f1117" }],
  },
];

export default function TimesheetDetail() {
  const { id } = useParams(); // Get timesheet ID from URL
  const location = useLocation();
  const navigate = useNavigate();
  const { fromProject, projectId, activeTab } = location.state || {};

  const [workOrders, setWorkOrders] = useState([]);
  const [editedWorkOrder, setEditedWorkOrder] = useState(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1, 2, 5, or 10
  const [projects, setProjects] = useState([]);
  const [editedProject, setEditedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [totalDistance, setTotalDistance] = useState(0);
  const [currentAnimationTime, setCurrentAnimationTime] = useState(null);

  // Revert-to-pending state
  const [reverting, setReverting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  // --- Scrubber/transport state ---
  const [playbackReady, setPlaybackReady] = useState(false);
  const [playbackTotalMs, setPlaybackTotalMs] = useState(0);
  const [playbackElapsedMs, setPlaybackElapsedMs] = useState(0);
  const [scrubberStops, setScrubberStops] = useState([]); // [{ pct, label }]
  const playStateRef = useRef({
    pts: null,
    timeline: null,
    totalMs: 0,
    elapsedMs: 0,
    entryIdx: 0,
    isScrubbing: false,
  });
  const [adjustmentBusy, setAdjustmentBusy] = useState(false);
  const markersRef = useRef([]);
  const pathRef = useRef(null);
  const originalMarkersRef = useRef([]);
  const originalPathRef = useRef(null);
  const triangleMarkerRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Custom time entry toggles — default is the 15-min time-slot dropdown
  const [customStartEntry, setCustomStartEntry] = useState(false);
  const [customEndEntry, setCustomEndEntry] = useState(false);

  // Builds a continuous chronological list of 15-minute time-of-day options
  // starting 3 hours before the anchor and running forward. If the anchor
  // isn't on the 15-min grid it's inserted inline at its correct position.
  const buildTimeSlotOptions = (anchorTimeStr) => {
    let anchorMinutes = 5 * 60; // fallback: 5:00 AM
    if (anchorTimeStr) {
      const [hh, mm] = anchorTimeStr.split(":").map(Number);
      if (!isNaN(hh) && !isNaN(mm)) anchorMinutes = hh * 60 + mm;
    }

    const LOOKBACK = 180; // 3 hours
    const startAbs = Math.floor((anchorMinutes - LOOKBACK) / 15) * 15;

    const buildOption = (abs) => {
      const normalized = ((abs % 1440) + 1440) % 1440;
      const h = Math.floor(normalized / 60);
      const m = normalized % 60;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const label = format(new Date(2000, 0, 1, h, m), "h:mm a");
      return { value, label };
    };

    const options = [];
    let anchorInserted = false;

    for (let i = 0; i < 96; i++) {
      const abs = startAbs + i * 15;

      if (!anchorInserted && anchorMinutes < abs) {
        options.push(buildOption(anchorMinutes));
        anchorInserted = true;
      }

      options.push(buildOption(abs));

      if (abs === anchorMinutes) anchorInserted = true;
    }

    if (!anchorInserted) options.push(buildOption(anchorMinutes));

    return options;
  };

  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  // Main timesheet state - fetched from server
  const [currentTimesheet, setCurrentTimesheet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Editable fields
  const [editedStart, setEditedStart] = useState("");
  const [editedEnd, setEditedEnd] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedUsePersonalVehicle, setEditedUsePersonalVehicle] =
    useState(null);
  const [editedBreaks, setEditedBreaks] = useState([]);

  // Timezone from timesheet or localStorage
  const userTimezone =
    currentTimesheet?.timezone || localStorage.getItem("userTimezone") || "UTC";

  // Format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = parseISO(dateString);
      const zonedDate = toZonedTime(date, userTimezone);
      return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      return "";
    }
  };

  // Just the date portion for the date input, e.g. "2026-06-21"
  const getDatePart = (dateString) => {
    if (!dateString) return "";
    try {
      const zonedDate = toZonedTime(parseISO(dateString), userTimezone);
      return format(zonedDate, "yyyy-MM-dd");
    } catch (error) {
      return "";
    }
  };

  // Just the time-of-day portion for the time-slot select, e.g. "06:15"
  const getTimePart = (dateString) => {
    if (!dateString) return "";
    try {
      const zonedDate = toZonedTime(parseISO(dateString), userTimezone);
      return format(zonedDate, "HH:mm");
    } catch (error) {
      return "";
    }
  };

  // Syncs every editable field from a server timesheet object
  const syncEditableFields = (ts) => {
    setEditedStart(ts.start?.$date || ts.start || "");
    setEditedEnd(ts.end?.$date || ts.end || "");
    setEditedDescription(ts.notes || "");
    setEditedUsePersonalVehicle(ts.usePersonalVehicle);
    setEditedBreaks(
      (ts.breaks || []).map((b) => ({ duration: b.duration || 0 })),
    );
  };

  // Fetch timesheet by ID on mount
  useEffect(() => {
    const fetchTimesheet = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data.success) {
          const ts = res.data.timesheet;
          setCurrentTimesheet(ts);
          syncEditableFields(ts);
        }
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to load timesheet",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchTimesheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/projects`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (res.data && res.data.projects) {
          setProjects(res.data.projects);
        } else if (Array.isArray(res.data)) {
          setProjects(res.data);
        }
      } catch (err) {
        console.error("Failed to load projects:", err);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Initialize editedProject when timesheet loads
  useEffect(() => {
    if (currentTimesheet?.project?._id) {
      setEditedProject(currentTimesheet.project._id);
    } else if (currentTimesheet?.project?.$oid) {
      setEditedProject(currentTimesheet.project.$oid);
    }
  }, [currentTimesheet?.project?._id, currentTimesheet?.project?.$oid]);

  // Fetch work orders when project changes
  useEffect(() => {
    const fetchWorkOrders = async () => {
      if (!editedProject) {
        setWorkOrders([]);
        return;
      }

      try {
        setLoadingWorkOrders(true);
        const token = localStorage.getItem("token");

        const res = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/projects/hourlyV2`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        let hourlyWorkOrders = [];

        if (Array.isArray(res.data)) {
          const matchingProject = res.data.find(
            (p) => p._id.toString() === editedProject.toString(),
          );
          if (matchingProject) {
            hourlyWorkOrders = matchingProject.workOrders || [];
          }
        }

        setWorkOrders(hourlyWorkOrders);
      } catch (err) {
        console.error("Failed to load work orders from hourlyV2:", err);
        setWorkOrders([]);
      } finally {
        setLoadingWorkOrders(false);
      }
    };

    fetchWorkOrders();
  }, [editedProject]);

  // Initialize editedWorkOrder when timesheet loads
  useEffect(() => {
    if (currentTimesheet?.workOrderId) {
      setEditedWorkOrder(currentTimesheet.workOrderId);
    } else if (currentTimesheet?.workOrder?._id) {
      setEditedWorkOrder(currentTimesheet.workOrder._id);
    } else if (currentTimesheet?.workOrder?.$oid) {
      setEditedWorkOrder(currentTimesheet.workOrder.$oid);
    }
  }, [
    currentTimesheet?.workOrderId,
    currentTimesheet?.workOrder?._id,
    currentTimesheet?.workOrder?.$oid,
  ]);

  const handleAddBreak = (minutes) => {
    if (!minutes || minutes <= 0) return;
    setEditedBreaks((prev) => [...prev, { duration: minutes }]);
  };
  const handleDeleteBreak = (index) => {
    setEditedBreaks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStartChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setEditedStart("");
      return;
    }
    try {
      const localDate = new Date(value);
      const utcDate = fromZonedTime(localDate, userTimezone);
      utcDate.setSeconds(0, 0);
      if (!isNaN(utcDate.getTime())) {
        setEditedStart(utcDate.toISOString());
      }
    } catch (error) {
      setEditedStart("");
    }
  };

  const handleEndChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setEditedEnd("");
      return;
    }
    try {
      const localDate = new Date(value);
      const utcDate = fromZonedTime(localDate, userTimezone);
      utcDate.setSeconds(0, 0);
      if (!isNaN(utcDate.getTime())) {
        setEditedEnd(utcDate.toISOString());
      }
    } catch (error) {
      setEditedEnd("");
    }
  };

  // Combine a date part + a time-of-day part (both local to userTimezone)
  // into the stored UTC ISO string
  const setDateTimePart = (field, datePart, timePart) => {
    if (!datePart || !timePart) return;
    try {
      const localDate = new Date(`${datePart}T${timePart}`);
      const utcDate = fromZonedTime(localDate, userTimezone);
      utcDate.setSeconds(0, 0);
      if (isNaN(utcDate.getTime())) return;

      if (field === "start") {
        setEditedStart(utcDate.toISOString());
      } else {
        setEditedEnd(utcDate.toISOString());
      }
    } catch (error) {
      // ignore malformed input
    }
  };

  const handleDatePartChange = (field, e) => {
    const current = field === "start" ? editedStart : editedEnd;
    const timePart = getTimePart(current) || "08:00";
    setDateTimePart(field, e.target.value, timePart);
  };

  const handleTimeSlotChange = (field, e) => {
    const current = field === "start" ? editedStart : editedEnd;
    const datePart = getDatePart(current) || format(new Date(), "yyyy-MM-dd");
    setDateTimePart(field, datePart, e.target.value);
  };

  const handleBreakChange = (index, value) => {
    const newBreaks = [...editedBreaks];
    newBreaks[index].duration = parseInt(value, 10) || "";
    setEditedBreaks(newBreaks);
  };

  const validateDateInput = (value, field) => {
    if (!value) return true;
    try {
      const date = parseISO(value);
      if (isNaN(date.getTime())) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Date",
          message: `Invalid ${field} time. Please enter a valid date and time.`,
        });
        return false;
      }
      return true;
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Invalid Date",
        message: `Invalid ${field} time.`,
      });
      return false;
    }
  };

  const calculateTimeDifference = () => {
    const startISO =
      editedStart || currentTimesheet?.start?.$date || currentTimesheet?.start;
    const endISO =
      editedEnd || currentTimesheet?.end?.$date || currentTimesheet?.end;

    if (!startISO) return "0.00";

    const start = parseISO(startISO);
    let end = endISO ? parseISO(endISO) : new Date();

    if (isNaN(start.getTime()) || (endISO && isNaN(end.getTime())))
      return "0.00";

    const diffMs = end - start;
    const totalBreakMs = editedBreaks.reduce(
      (sum, b) => sum + (Number(b.duration) || 0) * 60 * 1000,
      0,
    );

    const workHours = Math.max(0, (diffMs - totalBreakMs) / 1000 / 60 / 60);
    return workHours.toFixed(2);
  };

  const handleThumbnailClick = (url) => window.open(url, "_blank");

  // ---- Map rendering (routing pipeline defined above, outside the component) ----

  const loadOriginalMap = useCallback(
    async (map) => {
      if (!map || !window.google) return;
      if (!currentTimesheet?.locations?.length) return;

      setIsMapLoading(true);

      // Clear anything already on the map — polyline, markers, AND any
      // leftover animation artifacts.
      if (originalPathRef.current) {
        originalPathRef.current.setMap(null);
        originalPathRef.current = null;
      }
      if (pathRef.current) {
        pathRef.current.setMap(null);
        pathRef.current = null;
      }
      if (triangleMarkerRef.current) {
        triangleMarkerRef.current.setMap(null);
        triangleMarkerRef.current = null;
      }
      originalMarkersRef.current.forEach((m) => m.setMap(null));
      originalMarkersRef.current = [];
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      try {
        const validLocations = currentTimesheet.locations.filter(
          (loc) => loc.latitude && loc.longitude,
        );
        if (validLocations.length === 0) {
          setTotalDistance(0);
          return;
        }

        const routeCoordinates = await createTimeOrderedRoute(
          currentTimesheet.locations,
        );

        if (routeCoordinates.__failedLegs?.length) {
          setAlert({
            open: true,
            type: "warning",
            title: "Partial Route",
            message: `${routeCoordinates.__failedLegs.length} segment(s) of this route couldn't be road-snapped and are shown as straight lines.`,
          });
        }

        // Stationary — single pin
        if (routeCoordinates.length === 0) {
          const fallback = validLocations[0];
          const position = { lat: fallback.latitude, lng: fallback.longitude };
          const marker = new window.google.maps.Marker({
            position,
            map,
            title: "Worker location",
            icon: {
              url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
            },
          });
          originalMarkersRef.current.push(marker);
          map.setCenter(position);
          map.setZoom(16);
          setTotalDistance(0);
          return;
        }

        const cleanCoords = routeCoordinates.filter(
          (c) => !isNaN(c.lat) && !isNaN(c.lng) && c.lat !== 0 && c.lng !== 0,
        );
        const latLngPath = cleanCoords.map(
          (c) => new window.google.maps.LatLng(c.lat, c.lng),
        );

        const polyline = new window.google.maps.Polyline({
          path: latLngPath,
          strokeColor: "#f97316",
          strokeOpacity: 1,
          strokeWeight: 5,
          map,
        });
        // {lat,lng,timestamp} copy — timestamps drive the playback clock.
        polyline.snappedPath = cleanCoords.map((c) => ({
          lat: c.lat,
          lng: c.lng,
          timestamp:
            typeof c.timestamp === "number"
              ? c.timestamp
              : new Date(c.timestamp).getTime(),
        }));
        originalPathRef.current = polyline;

        const startMarker = new window.google.maps.Marker({
          position: latLngPath[0],
          map,
          title: "Start",
          icon: {
            url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
          },
        });
        const endMarker = new window.google.maps.Marker({
          position: latLngPath[latLngPath.length - 1],
          map,
          title: currentTimesheet?.end ? "End" : "Current",
          icon: { url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png" },
        });
        originalMarkersRef.current.push(startMarker, endMarker);

        const bounds = new window.google.maps.LatLngBounds();
        latLngPath.forEach((p) => bounds.extend(p));
        map.fitBounds(bounds, 64);

        const km = calculatePolylineDistance(routeCoordinates) / 1000;
        setTotalDistance(km);
      } catch (error) {
        console.error("Error creating map content:", error);
        setTotalDistance(0);
      } finally {
        setIsMapLoading(false);
      }
    },
    [currentTimesheet?.locations, currentTimesheet?.end],
  );

  // ---- Map init: single robust effect using the official loader -------------
  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      if (!currentTimesheet?.locations?.length) return;
      if (!mapContainerRef.current) return;

      try {
        const { Map } = await mapsLoader.importLibrary("maps");
        // Ensure geometry is ready before loadOriginalMap uses it.
        await mapsLoader.importLibrary("geometry");
        if (cancelled || !mapContainerRef.current) return;

        // Reuse existing map instance if the node is still alive.
        if (!mapRef.current) {
          mapRef.current = new Map(mapContainerRef.current, {
            center: {
              lat: currentTimesheet.locations[0].latitude,
              lng: currentTimesheet.locations[0].longitude,
            },
            zoom: 14,
            disableDefaultUI: true,
            zoomControl: true,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            clickableIcons: false,
            styles: DARK_MAP_STYLES,
          });
        }

        loadOriginalMap(mapRef.current);
      } catch (err) {
        console.error("Google Maps failed to load:", err);
        setAlert({
          open: true,
          type: "error",
          title: "Map Error",
          message: "Google Maps failed to load. Check your network/API key.",
        });
      }
    };

    initMap();

    return () => {
      cancelled = true;
    };
  }, [currentTimesheet?.locations, loadOriginalMap]);

  // Clear the map instance only when the component unmounts.
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        clearTimeout(animationFrameRef.current);
      }
      mapRef.current = null;
    };
  }, []);

  // Builds a playback timeline where every segment's on-screen duration is
  // proportional to its REAL elapsed time (scaled by one constant,
  // SPEED_FACTOR). `speedMultiplier` (1x/2x/5x/10x) divides the base factor
  // so relative pacing between segments never changes.
  const buildPlaybackTimeline = (pts, speedMultiplier = 1) => {
    const BASE_SPEED_FACTOR = 1 / 600; // 600x real-time at 1x
    const SPEED_FACTOR = BASE_SPEED_FACTOR / speedMultiplier;

    const MIN_SEGMENT_MS = 40; // never render a step faster than ~1 frame
    const MAX_DWELL_MS = 1500 / speedMultiplier; // cap one dwell's playback time

    const DWELL_REAL_MS = 60_000; // real gap that counts as a dwell
    const DWELL_MAX_DIST_M = 60; // ...only if the position barely moved

    const entries = [];

    for (let i = 1; i < pts.length; i++) {
      const d = haversineDistance(
        pts[i - 1].lat,
        pts[i - 1].lng,
        pts[i].lat,
        pts[i].lng,
      );
      const dtReal = pts[i].timestamp - pts[i - 1].timestamp;
      const isDwell = dtReal >= DWELL_REAL_MS && d < DWELL_MAX_DIST_M;

      let dur = Math.max(dtReal * SPEED_FACTOR, MIN_SEGMENT_MS);
      if (isDwell) dur = Math.min(dur, MAX_DWELL_MS);

      entries.push({ toIdx: i, d, isDwell, dur });
    }

    let cursor = 0;
    const timeline = entries.map((e) => {
      const entry = {
        playStart: cursor,
        playEnd: cursor + e.dur,
        toIdx: e.toIdx,
        tsStart: pts[e.toIdx - 1].timestamp,
        tsEnd: pts[e.toIdx].timestamp,
        isDwell: e.isDwell,
      };
      cursor += e.dur;
      return entry;
    });

    return { timeline, totalMs: Math.max(cursor, 1) };
  };

  // Rebuilds the timeline at a new speed multiplier, keeping playback at
  // the same point in the ACTUAL GPS timeline.
  const changePlaybackSpeed = (newSpeed) => {
    const state = playStateRef.current;
    if (!state.pts || !state.timeline) {
      setPlaybackSpeed(newSpeed);
      return;
    }

    const oldEntry = state.timeline[state.entryIdx];
    const oldSpan = Math.max(oldEntry.playEnd - oldEntry.playStart, 1);
    const oldFrac = Math.min(
      Math.max((state.elapsedMs - oldEntry.playStart) / oldSpan, 0),
      1,
    );
    const currentSimTime =
      oldEntry.tsStart + (oldEntry.tsEnd - oldEntry.tsStart) * oldFrac;

    const { timeline, totalMs } = buildPlaybackTimeline(state.pts, newSpeed);

    let idx = timeline.findIndex((e) => currentSimTime <= e.tsEnd);
    if (idx === -1) idx = timeline.length - 1;
    const entry = timeline[idx];
    const simSpan = Math.max(entry.tsEnd - entry.tsStart, 1);
    const simFrac = Math.min(
      Math.max((currentSimTime - entry.tsStart) / simSpan, 0),
      1,
    );
    const newElapsedMs =
      entry.playStart + simFrac * (entry.playEnd - entry.playStart);

    playStateRef.current = {
      ...state,
      timeline,
      totalMs,
      elapsedMs: newElapsedMs,
      entryIdx: idx,
    };

    setPlaybackSpeed(newSpeed);
    setPlaybackTotalMs(totalMs);
    renderAtElapsed(newElapsedMs);
  };

  const togglePauseAnimation = () => {
    if (!isAnimating) return;
    const resuming = isPausedRef.current;
    isPausedRef.current = !isPausedRef.current;
    setIsPaused(isPausedRef.current);
    if (resuming) startTicking(); // restart the loop from current elapsedMs
  };

  // Builds the drawable pts + playback timeline once, stores them in
  // playStateRef, and drops start/end markers + the growing polyline/arrow
  // on the map. Called once when Play is first pressed.
  const prepareAnimation = () => {
    if (
      !mapRef.current ||
      !originalPathRef.current?.snappedPath ||
      originalPathRef.current.snappedPath.length < 2
    ) {
      return false;
    }

    const map = mapRef.current;

    const rawLocs = currentTimesheet?.locations || [];
    const firstRaw = rawLocs[0]?.timestamp;
    const lastRaw = rawLocs[rawLocs.length - 1]?.timestamp;
    const fallbackStart = new Date(firstRaw?.$date || firstRaw).getTime();
    const fallbackEnd = new Date(lastRaw?.$date || lastRaw).getTime();

    const pts = ensureUsableTimestamps(
      originalPathRef.current.snappedPath,
      fallbackStart,
      fallbackEnd,
    );
    if (pts.length < 2 || isNaN(pts[0].timestamp)) return false;

    // Cleanup previous artifacts; hide static route during playback
    originalMarkersRef.current.forEach((m) => m.setMap(null));
    originalPathRef.current?.setMap(null);
    markersRef.current.forEach((m) => m.setMap(null));
    pathRef.current?.setMap(null);
    triangleMarkerRef.current?.setMap(null);
    markersRef.current = [];

    const startMarker = new window.google.maps.Marker({
      position: { lat: pts[0].lat, lng: pts[0].lng },
      map,
      icon: { url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png" },
      title: "Start",
      zIndex: 1000,
    });
    const endMarker = new window.google.maps.Marker({
      position: { lat: pts[pts.length - 1].lat, lng: pts[pts.length - 1].lng },
      map,
      icon: { url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png" },
      title: currentTimesheet?.end ? "End" : "Current",
      zIndex: 1000,
    });
    markersRef.current.push(startMarker, endMarker);

    const growing = new window.google.maps.Polyline({
      path: [new window.google.maps.LatLng(pts[0].lat, pts[0].lng)],
      strokeColor: "#ef4444",
      strokeOpacity: 1.0,
      strokeWeight: 6,
      map,
      zIndex: 100,
    });
    pathRef.current = growing;

    const arrow = new window.google.maps.Marker({
      position: { lat: pts[0].lat, lng: pts[0].lng },
      map,
      icon: {
        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 7,
        fillColor: "#ef4444",
        fillOpacity: 1,
        strokeWeight: 2.5,
        strokeColor: "#ffffff",
        rotation: 0,
      },
      zIndex: 2000,
    });
    triangleMarkerRef.current = arrow;

    const { timeline, totalMs } = buildPlaybackTimeline(pts, playbackSpeed);

    // Build scrubber stop-tick positions (% along totalMs) from dwell entries
    const stops = [];
    for (const e of timeline) {
      if (e.isDwell) {
        stops.push({
          pct: (e.playStart / totalMs) * 100,
          label: format(
            toZonedTime(new Date(e.tsStart), userTimezone),
            "h:mm a",
          ),
        });
      }
    }

    playStateRef.current = {
      pts,
      timeline,
      totalMs,
      elapsedMs: 0,
      entryIdx: 0,
      isScrubbing: false,
    };

    setScrubberStops(stops);
    setPlaybackTotalMs(totalMs);
    setPlaybackElapsedMs(0);
    setPlaybackReady(true);
    setCurrentAnimationTime(new Date(pts[0].timestamp));

    return true;
  };

  // Pure draw step: given elapsed playback ms, position the growing
  // polyline + arrow + clock label. Callable from the tick loop OR the
  // scrubber drag handler.
  const renderAtElapsed = (elapsedMs) => {
    const state = playStateRef.current;
    const { pts, timeline, totalMs } = state;
    if (!pts || !timeline) return;

    const clamped = Math.min(Math.max(elapsedMs, 0), totalMs);
    state.elapsedMs = clamped;

    let idx = 0;
    while (idx < timeline.length && timeline[idx].playEnd < clamped) idx++;
    idx = Math.min(idx, timeline.length - 1);
    state.entryIdx = idx;

    const growing = pathRef.current;
    const arrow = triangleMarkerRef.current;
    if (!growing || !arrow) return;

    // Redraw the polyline path from scratch since scrubbing can jump backwards.
    const path = growing.getPath();
    path.clear();
    path.push(new window.google.maps.LatLng(pts[0].lat, pts[0].lng));

    for (let i = 0; i <= idx; i++) {
      const e = timeline[i];
      const isCurrent = i === idx;
      if (!isCurrent || clamped >= e.playEnd) {
        path.push(
          new window.google.maps.LatLng(pts[e.toIdx].lat, pts[e.toIdx].lng),
        );
      }
    }

    const e = timeline[idx];
    const span = Math.max(e.playEnd - e.playStart, 1);
    const frac = Math.min(Math.max((clamped - e.playStart) / span, 0), 1);
    const from = pts[e.toIdx - 1];
    const to = pts[e.toIdx];

    if (e.isDwell) {
      arrow.setPosition(new window.google.maps.LatLng(from.lat, from.lng));
    } else {
      const lat = from.lat + (to.lat - from.lat) * frac;
      const lng = from.lng + (to.lng - from.lng) * frac;
      arrow.setPosition(new window.google.maps.LatLng(lat, lng));
      if (from.lat !== to.lat || from.lng !== to.lng) {
        const heading = window.google.maps.geometry
          ? window.google.maps.geometry.spherical.computeHeading(
              new window.google.maps.LatLng(from.lat, from.lng),
              new window.google.maps.LatLng(to.lat, to.lng),
            )
          : 0;
        arrow.setIcon({ ...arrow.getIcon(), rotation: heading });
      }
    }

    const simTime = e.tsStart + (e.tsEnd - e.tsStart) * frac;
    setCurrentAnimationTime(new Date(simTime));
    setPlaybackElapsedMs(clamped);
  };

  // The tick loop — advances elapsedMs and calls renderAtElapsed. Stops
  // itself at the end or when paused/scrubbing.
  const startTicking = () => {
    const intervalMs = 16;

    const tick = () => {
      const state = playStateRef.current;
      if (isPausedRef.current || state.isScrubbing) {
        animationFrameRef.current = setTimeout(tick, 100);
        return;
      }

      const next = state.elapsedMs + intervalMs;

      if (next >= state.totalMs) {
        renderAtElapsed(state.totalMs);
        finishAnimation();
        return;
      }

      renderAtElapsed(next);
      animationFrameRef.current = setTimeout(tick, intervalMs);
    };

    animationFrameRef.current = setTimeout(tick, intervalMs);
  };

  const finishAnimation = () => {
    setIsAnimating(false);
    setIsPaused(false);
    setTimeout(() => {
      triangleMarkerRef.current?.setMap(null);
      pathRef.current?.setMap(null);
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
      triangleMarkerRef.current = null;
      pathRef.current = null;
      setPlaybackReady(false);
      setCurrentAnimationTime(null);
      if (mapRef.current) loadOriginalMap(mapRef.current);
    }, 400);
  };

  // Public entry point — Play button
  const animatePath = () => {
    if (isAnimating) return;
    const ok = prepareAnimation();
    if (!ok) return;
    isPausedRef.current = false;
    setIsPaused(false);
    setIsAnimating(true);
    startTicking();
  };

  // Scrubber drag handler — jump to an arbitrary point in the timeline.
  const handleScrub = (ms) => {
    const state = playStateRef.current;
    if (!state.pts) {
      const ok = prepareAnimation();
      if (!ok) return;
      isPausedRef.current = true;
      setIsPaused(true);
      setIsAnimating(true);
    }
    playStateRef.current.isScrubbing = true;
    renderAtElapsed(ms);
  };

  const handleScrubEnd = () => {
    playStateRef.current.isScrubbing = false;
  };

  // Guarantees the playback points carry a usable, monotonic timeline.
  const ensureUsableTimestamps = (pts, fallbackStartTs, fallbackEndTs) => {
    const numeric = pts.map((p) => ({
      ...p,
      timestamp:
        typeof p.timestamp === "number" && !isNaN(p.timestamp)
          ? p.timestamp
          : typeof p.timestamp === "string" || p.timestamp instanceof Date
            ? new Date(p.timestamp).getTime()
            : NaN,
    }));

    const valid = numeric.filter((p) => !isNaN(p.timestamp));
    const span =
      valid.length >= 2
        ? valid[valid.length - 1].timestamp - valid[0].timestamp
        : 0;

    const healthy = valid.length === numeric.length && span > 60_000;

    if (healthy) {
      // Clamp monotonic so one out-of-order point can't stall the sweep
      const out = [];
      for (const p of numeric) {
        const last = out[out.length - 1];
        out.push(
          last && p.timestamp < last.timestamp
            ? { ...p, timestamp: last.timestamp }
            : p,
        );
      }
      return out;
    }

    console.warn(
      `Playback timestamps degenerate (valid ${valid.length}/${numeric.length}, span ${Math.round(span / 1000)}s) — rebuilding by distance`,
    );
    return interpolateSegmentTimestamps(
      numeric.map(({ lat, lng }) => ({ lat, lng })),
      fallbackStartTs,
      fallbackEndTs,
    );
  };

  const handleSave = async () => {
    if (!validateDateInput(editedStart, "start")) return;
    if (editedEnd && !validateDateInput(editedEnd, "end")) return;

    const payload = {
      id: currentTimesheet._id,
      start: editedStart || null,
      end: editedEnd || null,
      notes: editedDescription,
      usePersonalVehicle: editedUsePersonalVehicle,
      breaks: editedBreaks.map((b) => ({
        duration: parseInt(b.duration, 10) || 0,
      })),
      timezone: userTimezone,
      project: editedProject,
      timezoneOffset:
        getTimezoneOffset(userTimezone, new Date()) / 1000 / 60 / 60,
      workOrder: editedWorkOrder,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/update",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        const updated = response.data.timesheet;
        setCurrentTimesheet(updated);
        syncEditableFields(updated);

        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Saved!",
        });
      }
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.message || error.message,
      });
    }
  };

  const handleApproveAdjustment = async () => {
    setAdjustmentBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/${currentTimesheet._id}/approve-adjustment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        const updated = res.data.timesheet;
        setCurrentTimesheet(updated);
        setEditedStart(updated.start?.$date || updated.start || "");
        setEditedEnd(updated.end?.$date || updated.end || "");
        setAlert({
          open: true,
          type: "success",
          title: "Adjustment Approved",
          message: "The requested changes have been applied.",
        });
      }
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setAdjustmentBusy(false);
    }
  };

  const handleDenyAdjustment = async () => {
    setAdjustmentBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/${currentTimesheet._id}/deny-adjustment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setCurrentTimesheet(res.data.timesheet);
        setAlert({
          open: true,
          type: "success",
          title: "Adjustment Denied",
          message: "The adjustment request has been denied.",
        });
      }
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setAdjustmentBusy(false);
    }
  };

  const goBackToList = () => {
    if (fromProject && projectId) {
      navigate(`/Projects/${projectId}`, {
        state: {
          initialTab: "Timesheets",
          timesheetSubTab: activeTab || "Pending",
        },
      });
    } else {
      navigate("/timesheets", {
        state: { timesheetSubTab: activeTab || "Pending" },
      });
    }
  };

  const handleApprove = async () => {
    try {
      const token = localStorage.getItem("token");
      if (
        editedStart !==
          (currentTimesheet.start?.$date || currentTimesheet.start || "") ||
        editedEnd !==
          (currentTimesheet.end?.$date || currentTimesheet.end || "") ||
        editedDescription !== (currentTimesheet.notes || "") ||
        editedUsePersonalVehicle !== currentTimesheet.usePersonalVehicle ||
        JSON.stringify(editedBreaks) !==
          JSON.stringify(
            (currentTimesheet.breaks || []).map((b) => ({
              duration: b.duration || 0,
            })),
          )
      ) {
        await handleSave();
      }

      await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/approve",
        { id: currentTimesheet._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setAlert({
        open: true,
        type: "success",
        title: "Approved",
        message: "Timesheet approved!",
      });

      goBackToList();
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.message || error.message,
      });
    }
  };

  const handleReject = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/reject",
        { id: currentTimesheet._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setAlert({
        open: true,
        type: "success",
        title: "Rejected",
        message: "Timesheet rejected.",
      });

      goBackToList();
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: error.response?.data?.message || error.message,
      });
    }
  };

  const handleRevertToPending = async () => {
    setReverting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/${currentTimesheet._id}/revert-to-pending`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setCurrentTimesheet(res.data.timesheet);
        syncEditableFields(res.data.timesheet);
        setAlert({
          open: true,
          type: "success",
          title: "Reverted",
          message: "Timesheet is back to pending.",
        });
      }
    } catch (error) {
      setAlert({
        open: true,
        type: "error",
        title: "Cannot Revert",
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setReverting(false);
      setShowRevertConfirm(false);
    }
  };

  const handleBack = goBackToList;

  // ─────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-900 rounded-2xl">
        <div className="flex items-center gap-3 text-gray-300">
          <Spinner />
          <span className="text-sm font-medium tracking-wide">
            Loading timesheet…
          </span>
        </div>
      </div>
    );
  }

  if (!currentTimesheet) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-gray-900 rounded-2xl text-center px-4">
        <p className="text-red-400 text-lg font-semibold">
          Timesheet not found
        </p>
        <button
          onClick={handleBack}
          className="mt-4 px-6 py-2 bg-orange-400 text-gray-900 font-semibold rounded-lg hover:bg-orange-300 transition"
        >
          Go Back
        </button>
      </div>
    );
  }

  const {
    locations = [],
    images = [],
    receipts = [],
    Rejected,
    status,
    adjustmentRequest,
    payrollLocked,
  } = currentTimesheet;

  const hasPendingAdjustment =
    adjustmentRequest?.requested && adjustmentRequest?.status === "pending";

  const isPending = !status && !Rejected;
  const isRejected = !!Rejected;
  const isApproved = !!status && !Rejected;
  const statusLabel = isPending
    ? "Pending"
    : isRejected
      ? "Rejected"
      : "Approved";
  const statusStyles = isPending
    ? "bg-yellow-400/10 text-yellow-400 border-yellow-400/30"
    : isRejected
      ? "bg-red-400/10 text-red-400 border-red-400/30"
      : "bg-green-400/10 text-green-400 border-green-400/30";

  // Approved + locked to a payroll period → can't revert (server enforces too)
  const revertBlocked = isApproved && !!payrollLocked;

  const isDirty =
    editedStart !==
      (currentTimesheet.start?.$date || currentTimesheet.start || "") ||
    editedEnd !== (currentTimesheet.end?.$date || currentTimesheet.end || "") ||
    editedDescription !== (currentTimesheet.notes || "") ||
    editedUsePersonalVehicle !== currentTimesheet.usePersonalVehicle ||
    JSON.stringify(editedBreaks) !==
      JSON.stringify(
        (currentTimesheet.breaks || []).map((b) => ({
          duration: b.duration || 0,
        })),
      );

  const totalBreakMins = editedBreaks.reduce(
    (s, b) => s + (Number(b.duration) || 0),
    0,
  );

  const hasCosts =
    (currentTimesheet.totalCost || 0) > 0 ||
    (currentTimesheet.subtotal || 0) > 0;

  const renderTimeField = (
    field,
    label,
    value,
    custom,
    setCustom,
    onCustomChange,
  ) => (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setCustom((v) => !v)}
          disabled={!!payrollLocked}
          className="text-[11px] text-orange-400 hover:text-orange-300 font-medium disabled:text-gray-600 disabled:cursor-not-allowed"
        >
          {custom ? "Use 15-min slots" : "Custom time"}
        </button>
      </div>
      {custom ? (
        <input
          type="datetime-local"
          step={60}
          value={formatDateForInput(value)}
          onChange={onCustomChange}
          className={INPUT_BASE}
        />
      ) : (
        <div className="grid grid-cols-5 gap-2">
          <input
            type="date"
            value={getDatePart(value)}
            onChange={(e) => handleDatePartChange(field, e)}
            className={`${INPUT_BASE} col-span-3 min-w-0`}
          />
          <select
            value={getTimePart(value)}
            onChange={(e) => handleTimeSlotChange(field, e)}
            className={`${INPUT_BASE} col-span-2 min-w-0`}
          >
            {buildTimeSlotOptions(getTimePart(value)).map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                className="bg-gray-900 text-gray-100"
              >
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-900 rounded-2xl p-4 sm:p-5 mt-2 mx-4">
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => setAlert({ ...alert, open: false })}
        confirmText="OK"
      />

      {/* Revert confirmation modal */}
      {showRevertConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-6">
            <h3 className="text-base font-bold text-white mb-2">
              Revert to Pending?
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              {isApproved
                ? "This removes the approval and takes the hours out of the project's approved totals. It will need to be approved again."
                : "This clears the rejection and puts the timesheet back in the pending queue."}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowRevertConfirm(false)}
                disabled={reverting}
                className="px-4 py-2 text-sm font-semibold text-gray-200 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRevertToPending}
                disabled={reverting}
                className="px-4 py-2 text-sm font-semibold bg-orange-400 text-gray-900 rounded-lg hover:bg-orange-300 transition disabled:opacity-50"
              >
                {reverting ? "Reverting…" : "Revert to Pending"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className={`${SECTION_CARD} px-5 py-4 mb-4 flex flex-wrap items-center justify-between gap-4`}
      >
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={handleBack}
            className="shrink-0 px-3 py-2 text-sm font-semibold text-gray-200 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition"
          >
            ← Back
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-white tracking-tight truncate">
                {currentTimesheet.displayName || "Timesheet"}
              </h1>
              <span
                className={`px-2.5 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider ${statusStyles}`}
              >
                {statusLabel}
              </span>
              {payrollLocked && (
                <span className="px-2.5 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider bg-amber-400/10 text-amber-300 border-amber-400/30">
                  🔒 Payroll
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate mt-0.5">
              {currentTimesheet.email || "N/A"}
              {getDatePart(editedStart) && ` · ${getDatePart(editedStart)}`}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <StatTile label="Hours" value={calculateTimeDifference()} accent />
          <StatTile label="Breaks" value={totalBreakMins} unit="min" />
          <StatTile
            label="Distance"
            value={totalDistance.toFixed(1)}
            unit="km"
          />
        </div>
      </div>

      {/* Payroll lock banner */}
      {payrollLocked && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-gray-700/60 border-l-4 border-l-amber-400 bg-gray-800 px-5 py-3">
          <svg
            className="w-5 h-5 text-amber-400 shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-300">
              Locked to a payroll period
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              Editing is disabled and this timesheet can't be reverted to
              pending until the pay period is unlocked.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* LEFT — editable details (disabled as a group when payroll-locked) */}
        <fieldset
          disabled={!!payrollLocked}
          className="lg:col-span-2 min-w-0 m-0 p-0 border-0 space-y-5"
        >
          {/* Assignment */}
          <div className={`${SECTION_CARD} p-5`}>
            <CardHeader title="Assignment" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={LABEL_BASE}>Project</label>
                <select
                  value={editedProject || ""}
                  onChange={(e) => {
                    setEditedProject(e.target.value);
                    setEditedWorkOrder(null);
                  }}
                  className={INPUT_BASE}
                  disabled={loadingProjects}
                >
                  {loadingProjects ? (
                    <option className="bg-gray-900">Loading...</option>
                  ) : (
                    <>
                      <option value="" className="bg-gray-900">
                        Select Project
                      </option>
                      {projects.map((proj) => (
                        <option
                          key={proj._id}
                          value={proj._id}
                          className="bg-gray-900 text-gray-100"
                        >
                          {proj.projectName}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className={LABEL_BASE}>Work Order</label>
                <select
                  value={editedWorkOrder || ""}
                  onChange={(e) => setEditedWorkOrder(e.target.value)}
                  className={INPUT_BASE}
                  disabled={loadingWorkOrders || !editedProject}
                >
                  {loadingWorkOrders ? (
                    <option className="bg-gray-900">Loading...</option>
                  ) : !editedProject ? (
                    <option className="bg-gray-900">
                      Select a project first
                    </option>
                  ) : workOrders.length === 0 ? (
                    <option className="bg-gray-900">
                      No hourly work orders available
                    </option>
                  ) : (
                    <>
                      <option value="" className="bg-gray-900">
                        Select Work Order
                      </option>
                      {workOrders.map((wo) => (
                        <option
                          key={wo._id}
                          value={wo._id}
                          className="bg-gray-900 text-gray-100"
                        >
                          WO# {wo.woNumber} - {wo.title}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* Time */}
          <div className={`${SECTION_CARD} p-5`}>
            <CardHeader
              title="Time"
              right={
                <span className="text-[11px] text-gray-500">
                  {userTimezone}
                </span>
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderTimeField(
                "start",
                "Start",
                editedStart,
                customStartEntry,
                setCustomStartEntry,
                handleStartChange,
              )}
              {renderTimeField(
                "end",
                "End",
                editedEnd,
                customEndEntry,
                setCustomEndEntry,
                handleEndChange,
              )}
            </div>

            {/* Breaks */}
            <div className="mt-5 pt-5 border-t border-gray-700/60">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <label className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Breaks
                </label>
                <div className="flex gap-1.5">
                  {[15, 30, 45, 60].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => handleAddBreak(m)}
                      className="px-2.5 py-1 text-[11px] font-semibold text-orange-300 bg-gray-900 border border-gray-700 rounded-md hover:border-orange-400/60 hover:text-orange-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      + {m}m
                    </button>
                  ))}
                </div>
              </div>
              {editedBreaks.length === 0 ? (
                <p className="text-xs text-gray-500">No breaks recorded.</p>
              ) : (
                <div className="space-y-2">
                  {editedBreaks.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={b.duration}
                        onChange={(e) => handleBreakChange(i, e.target.value)}
                        className={`${INPUT_BASE} w-28`}
                      />
                      <span className="text-xs text-gray-400">minutes</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteBreak(i)}
                        className="ml-auto px-2.5 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-400/10 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className={`${SECTION_CARD} p-5`}>
            <CardHeader title="Notes" />
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Add notes about this shift…"
              className={`${INPUT_BASE} h-24 resize-none`}
            />
          </div>
        </fieldset>

        {/* RIGHT — adjustment, map, costs, attachments */}
        <div className="space-y-5 min-w-0">
          {hasPendingAdjustment && (
            <div className="bg-gray-800 rounded-xl shadow-md border border-amber-500/40 overflow-hidden">
              <div className="bg-gray-900/60 border-b border-amber-500/30 px-5 py-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-amber-400 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                <h2 className="text-sm font-bold text-amber-400 uppercase tracking-wider">
                  Adjustment Requested
                </h2>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-200 leading-snug">
                  {adjustmentRequest.reason}
                </p>

                <div className="space-y-2">
                  {adjustmentRequest.requestedStart && (
                    <div className="flex items-center justify-between bg-gray-900 rounded-lg px-3 py-2 border border-gray-700">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                        Requested Start
                      </span>
                      <span className="text-sm text-amber-200 font-medium">
                        {format(
                          toZonedTime(
                            parseISO(
                              adjustmentRequest.requestedStart?.$date ||
                                adjustmentRequest.requestedStart,
                            ),
                            userTimezone,
                          ),
                          "MMM d, h:mm a",
                        )}
                      </span>
                    </div>
                  )}
                  {adjustmentRequest.requestedEnd && (
                    <div className="flex items-center justify-between bg-gray-900 rounded-lg px-3 py-2 border border-gray-700">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                        Requested End
                      </span>
                      <span className="text-sm text-amber-200 font-medium">
                        {format(
                          toZonedTime(
                            parseISO(
                              adjustmentRequest.requestedEnd?.$date ||
                                adjustmentRequest.requestedEnd,
                            ),
                            userTimezone,
                          ),
                          "MMM d, h:mm a",
                        )}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleApproveAdjustment}
                    disabled={adjustmentBusy}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Approve
                  </button>
                  <button
                    onClick={handleDenyAdjustment}
                    disabled={adjustmentBusy}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold bg-gray-700 text-gray-200 border border-gray-600 rounded-lg hover:bg-gray-600 hover:border-red-500/50 hover:text-red-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Deny
                  </button>
                </div>

                <p className="text-[11px] text-gray-500 text-center">
                  The timesheet can't be approved until this request is
                  resolved.
                </p>
              </div>
            </div>
          )}

          {/* Map */}
          <div className={`${SECTION_CARD} p-5`}>
            <CardHeader
              title="Location Path"
              right={
                <button
                  onClick={animatePath}
                  disabled={isAnimating || locations.length < 2}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg transition bg-orange-400 text-gray-900 hover:bg-orange-300 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  {isAnimating ? "Playing…" : "▶ Play Path"}
                </button>
              }
            />

            {isAnimating && currentAnimationTime && (
              <div
                className={`mb-3 p-2 rounded-lg text-center ${
                  isPaused ? "bg-gray-600" : "bg-orange-400"
                }`}
              >
                <p
                  className={`text-sm font-bold ${
                    isPaused ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  {format(
                    toZonedTime(currentAnimationTime, userTimezone),
                    "h:mm:ss a",
                  )}
                  {isPaused && "  (paused)"}
                </p>
              </div>
            )}

            {/* Map container is ALWAYS mounted so the ref is stable. */}
            <div className="relative">
              <div
                id="map"
                ref={mapContainerRef}
                className="rounded-lg overflow-hidden border border-gray-700 bg-gray-900"
                style={{ width: "100%", height: "20rem" }}
              ></div>
              {isMapLoading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-900/80">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Spinner />
                    <span className="text-sm">Loading route…</span>
                  </div>
                </div>
              )}
              {!isMapLoading &&
                (!currentTimesheet?.locations ||
                  currentTimesheet.locations.length === 0) && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-900/80">
                    <p className="text-sm text-gray-500">
                      No route data available.
                    </p>
                  </div>
                )}
            </div>

            {/* --- Transport / scrubber strip --- */}
            {playbackReady && (
              <div className="mt-3 bg-gray-900 rounded-lg border border-gray-700 px-3 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={togglePauseAnimation}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-orange-400 text-gray-900 hover:bg-orange-300 transition font-bold"
                    aria-label={isPaused ? "Resume" : "Pause"}
                  >
                    {isPaused ? "▶" : "⏸"}
                  </button>

                  <div className="flex-1 min-w-[140px] relative pt-3">
                    <div className="absolute top-0 left-0 right-0 h-3 pointer-events-none">
                      {scrubberStops.map((s, i) => (
                        <div
                          key={i}
                          title={s.label}
                          className="absolute top-0 w-0.5 h-2.5 bg-orange-400/70"
                          style={{ left: `${s.pct}%` }}
                        />
                      ))}
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={playbackTotalMs}
                      step={16}
                      value={playbackElapsedMs}
                      onChange={(e) => handleScrub(Number(e.target.value))}
                      onMouseUp={handleScrubEnd}
                      onTouchEnd={handleScrubEnd}
                      className="w-full accent-orange-400 cursor-pointer"
                    />
                  </div>

                  <span className="shrink-0 text-xs font-semibold text-gray-300 tabular-nums w-20 text-right">
                    {currentAnimationTime &&
                      format(
                        toZonedTime(currentAnimationTime, userTimezone),
                        "h:mm:ss a",
                      )}
                  </span>
                </div>

                <div className="flex gap-1 mt-3 justify-end">
                  {[1, 2, 5, 10].map((s) => (
                    <button
                      key={s}
                      onClick={() => changePlaybackSpeed(s)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-md transition ${
                        playbackSpeed === s
                          ? "bg-orange-400 text-gray-900"
                          : "bg-gray-800 text-gray-400 hover:text-gray-200 border border-gray-700"
                      }`}
                    >
                      {s}x
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Cost summary */}
          {hasCosts && (
            <div className={`${SECTION_CARD} p-5`}>
              <CardHeader title="Cost Summary" />
              <div className="grid grid-cols-3 gap-2">
                {[
                  ["Subtotal", currentTimesheet.subtotal],
                  ["Burden", currentTimesheet.burdenCost],
                  ["WSIB", currentTimesheet.wsibCost],
                ].map(([label, val]) => (
                  <div
                    key={label}
                    className="bg-gray-900 border border-gray-700/70 rounded-lg px-3 py-2"
                  >
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      {label}
                    </p>
                    <p className="text-sm font-semibold text-gray-100 tabular-nums">
                      {fmtMoney(val)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between bg-gray-900 border border-orange-400/30 rounded-lg px-4 py-3">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Total Cost
                </span>
                <span className="text-lg font-bold text-orange-400 tabular-nums">
                  {fmtMoney(currentTimesheet.totalCost)}
                </span>
              </div>
            </div>
          )}

          {(images.length > 0 || receipts.length > 0) && (
            <div className={`${SECTION_CARD} p-5`}>
              <CardHeader
                title={`Attachments (${images.length + receipts.length})`}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {images.map((img, i) => (
                  <img
                    key={`img-${i}`}
                    src={img.url}
                    alt="Image"
                    className="w-full h-28 object-cover rounded-lg border border-gray-700 cursor-pointer hover:opacity-80 hover:border-orange-400 transition"
                    onClick={() => handleThumbnailClick(img.url)}
                  />
                ))}
                {receipts.map((r, i) => (
                  <img
                    key={`rec-${i}`}
                    src={r.url}
                    alt="Receipt"
                    className="w-full h-28 object-cover rounded-lg border border-gray-700 cursor-pointer hover:opacity-80 hover:border-orange-400 transition"
                    onClick={() => handleThumbnailClick(r.url)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action bar — a floating card that sticks to the bottom of the
          scrolling area and stays inside the page padding. */}
      <div className="sticky bottom-3 z-30 mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-700 bg-gray-800/95 backdrop-blur px-4 py-3 shadow-2xl">
        <p className="text-xs">
          {payrollLocked ? (
            <span className="text-amber-300 font-medium">
              🔒 Locked to payroll
            </span>
          ) : isDirty ? (
            <span className="text-orange-400 font-semibold">
              ● Unsaved changes
            </span>
          ) : (
            <span className="text-gray-500">All changes saved</span>
          )}
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSave}
            disabled={!isDirty || !!payrollLocked}
            className="px-5 py-2 text-sm font-semibold rounded-lg transition bg-orange-400 text-gray-900 hover:bg-orange-300 disabled:bg-gray-700 disabled:text-gray-500 disabled:hover:bg-gray-700 disabled:cursor-not-allowed"
          >
            Save
          </button>

          {(isApproved || isRejected) && (
            <button
              onClick={() => setShowRevertConfirm(true)}
              disabled={revertBlocked || reverting}
              title={
                revertBlocked
                  ? "Locked to a payroll period — unlock the pay period first"
                  : "Send back to the pending queue"
              }
              className="px-5 py-2 text-sm font-semibold rounded-lg border transition bg-gray-900 text-orange-300 border-orange-400/40 hover:bg-orange-400/10 disabled:bg-gray-900 disabled:text-gray-500 disabled:border-gray-700 disabled:hover:bg-gray-900 disabled:cursor-not-allowed"
            >
              {revertBlocked ? "🔒 Revert to Pending" : "↺ Revert to Pending"}
            </button>
          )}

          {!isApproved && (
            <button
              onClick={handleApprove}
              disabled={hasPendingAdjustment}
              title={
                hasPendingAdjustment
                  ? "Resolve the pending adjustment request first"
                  : ""
              }
              className="px-5 py-2 text-sm font-semibold rounded-lg transition bg-green-500 text-white hover:bg-green-400 disabled:bg-gray-700 disabled:text-gray-500 disabled:hover:bg-gray-700 disabled:cursor-not-allowed"
            >
              Approve
            </button>
          )}

          {isPending && (
            <button
              onClick={handleReject}
              className="px-5 py-2 text-sm font-semibold bg-red-500 text-white rounded-lg hover:bg-red-400 transition"
            >
              Reject
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
